import { useState } from 'react';
import Back from 'components/Back';
import {
  Button,
  Card, Checkboxes, Container, Form,
} from 'nhsuk-react-components';
import { useNavigate, useParams } from 'react-router-dom';
import useFetchTeamMembers, { Role } from 'queries/fetch/fetchTeamMembers.ts';
import ProgressIndicator from 'components/ProgressIndicator';
import useFetchTeamDetails from 'queries/fetch/fetchTeamDetails.ts';
import { CognitoUser } from 'schemas/UserInfo.schema.ts';
import useUpdateUserPermissions from 'queries/mutate/updateUserPermissions.ts';
import ApiErrorDetails from 'components/ApiErrorDetails';
import useUser from 'hooks/useUser.ts';
import { IManageUserPageParams } from 'utils/parameterUtils.ts';
import { getRolesForUser, RolesWithDescription } from 'utils/userPermissions.ts';

interface IEditPermissionsFormProps {
    currentUser: CognitoUser,
    rolesForUser: [string, string][],
    teamName: string,
    teamId: string,
}

const getUserDisplayName = (user: CognitoUser): string => {
  if (user.given_name && user.family_name) {
    return `${user.given_name} ${user.family_name}`;
  }

  return user.name;
};

function EditPermissionsForm({
  currentUser,
  rolesForUser,
  teamName,
  teamId,
}: IEditPermissionsFormProps) {
  const loggedInUser = useUser();
  const navigate = useNavigate();
  const updatePermissions = useUpdateUserPermissions();
  const [permissions, setPermissions] = useState<Role[]>(rolesForUser.map((r) => r[0] as Role));
  const [error, setError] = useState<Error | null>(null);

  return (
    <>
      <Card>
        <>
          <h2 className="nhsuk-heading-s nhsuk-u-margin-bottom-4">User permissions</h2>

          <Form>
            <Checkboxes
              hint={`What should ${getUserDisplayName(currentUser)} be able to do in ${teamName}?`}
            >
              {
              Object.entries(RolesWithDescription)
                .map(([role, description]) => {
                  // Removing `team_admin` from yourself will break the page.
                  const isSelfAndTeamAdminRole = currentUser.sub === loggedInUser.userId && role === 'team_admin';

                  return (
                    <Checkboxes.Item
                      key={role}
                      value={role}
                      defaultChecked={Boolean(permissions.find((r) => r === role))}
                      disabled={role === 'read' || isSelfAndTeamAdminRole}
                      onChange={(event) => {
                        const newPermission = event.currentTarget.value as Role;
                        if (permissions.includes(newPermission)) {
                          setPermissions([...permissions].filter((p) => p !== event.currentTarget.value));
                          return;
                        }

                        setPermissions([...permissions as Role[], newPermission]);
                      }}
                    >
                      {description}
                    </Checkboxes.Item>
                  );
                })
            }
            </Checkboxes>
          </Form>
        </>
      </Card>

      <Button onClick={async () => {
        setError(null);
        try {
          await updatePermissions.mutateAsync({
            userId: currentUser.sub,
            teamId,
            permissions,
          });
        } catch (updatePermissionsError) {
          setError(updatePermissionsError as Error);
          return;
        }

        navigate(`/admin/${teamId}/manage-user-details/user/${currentUser.sub}`);
      }}
      >
        Save and continue
      </Button>
      <ApiErrorDetails apiError={error} />
    </>
  );
}

function EditUserPermissionsPage() {
  const { teamId, userId } = useParams() as IManageUserPageParams;
  const teamDetails = useFetchTeamDetails({ teamId });
  const teamMembers = useFetchTeamMembers();
  const currentUser = teamMembers.data?.[teamId].read.find((t) => t.sub === userId);

  if (!teamMembers.data || !teamDetails.data || !currentUser) {
    return <ProgressIndicator />;
  }

  return (
    <Container>
      <div className="nhsuk-u-margin-top-6 nhsuk-u-margin-bottom-6">
        <Back label="Back to user" backTo={`/admin/${teamId}/manage-user-details/user/${currentUser.sub}`} />
      </div>

      <EditPermissionsForm
        currentUser={currentUser}
        rolesForUser={getRolesForUser(currentUser, teamMembers.data[teamId])}
        teamName={teamDetails.data.team.TEAM_NAME}
        teamId={teamId}
      />
    </Container>
  );
}

export default EditUserPermissionsPage;
